import React from "react";
import { StarFilled } from "@ant-design/icons";
import "./testimonials.component.styles.scss";
import { conversions } from "../../../lib/analytics";
import { useLang } from "../../../lib/i18n";
import { useReveal } from "../../../lib/motion";

type Lang = "it" | "en";
type Localized = string | { it: string; en: string };

type Review = {
  rating: number;
  body: Localized;
  name: string;
  service: Localized;
  when: Localized;
  feature?: boolean;
};

const pick = (v: Localized, lang: Lang): string =>
  typeof v === "string" ? v : v[lang];

// Verbatim reviews from Treatwell and Google.
const reviews: Review[] = [
  {
    rating: 5,
    body: {
      it: "Regina mi ha subito fatta sentire a mio agio. Le ho spiegato le mie esigenze e ha capito come farsi rilassare sia fisicamente che mentalmente, con un fantastico massaggio di oltre 1 ora. Ci mette passione e dedizione. Raccomanda.",
      en: "Regina put me at ease right away. I told her what I needed and she understood how to help me relax both physically and mentally, with a wonderful massage of more than an hour. She brings real passion and dedication. Highly recommend.",
    },
    name: "Arianna",
    service: { it: "Massaggio Ayurvedico", en: "Ayurvedic Massage" },
    when: { it: "4 mesi fa", en: "4 months ago" },
    feature: true,
  },
  {
    rating: 5,
    body: {
      it: "Prima visita! Un avviso: la prenotazione tramite Treatwell potrebbe essere problematica — il mio appuntamento non risultava dalla loro parte. Nonostante questo, lo staff è stato meraviglioso ed è riuscito a trovarmi spazio. Avevo un forte mal di schiena e non riuscivo a girarmi a destra. Il massaggio è stato perfetto e il dolore è completamente sparito. Grazie per l'ottimo servizio e l'atmosfera accogliente. Tornerò sicuramente!",
      en: "First time visitor! A heads-up: booking via Treatwell might be tricky as my appointment didn't show up on their end. Regardless, the staff was wonderful and managed to fit me in. I had a really bad backache and couldn't turn to the right at all. The massage was perfect, and the pain is completely gone. Thank you for the great service and friendly atmosphere. I will definitely return!",
    },
    name: "Polina Stetsenko",
    service: {
      it: "Massaggio Decontratturante · Google",
      en: "Deep-Tissue Massage · Google",
    },
    when: { it: "2 giorni fa", en: "2 days ago" },
  },
  {
    rating: 5,
    body: {
      it: "Mi sono rivolta a Regina per una fastidiosa sensazione di contrattura al trapezio. Regina è accogliente e attenta. Con il suo massaggio total body mi sono sentita meglio fin da fine seduta. Massaggio piacevole e funzionale, terminato con una buona tisana per completare il relax.",
      en: "I went to Regina for an annoying tightness in my trapezius. Regina is warm and attentive. By the end of her full-body massage I already felt better. A pleasant, effective massage, finished with a nice herbal tea to complete the relaxation.",
    },
    name: "stefania",
    service: {
      it: "Massaggio Decontratturante",
      en: "Deep-Tissue Massage",
    },
    when: { it: "circa un anno fa", en: "about a year ago" },
  },
  {
    rating: 5,
    body: { it: "La adoro!", en: "I adore her!" },
    name: "Antonella",
    service: { it: "Ceretta gambe", en: "Leg Waxing" },
    when: { it: "circa un anno fa", en: "about a year ago" },
  },
  {
    rating: 5,
    body: {
      it: "Consiglio assolutamente il centro di Regina. Durante il trattamento è stata molto attenta e precisa. Locale pulito.",
      en: "I absolutely recommend Regina's studio. During the treatment she was very attentive and precise. Clean, well-kept space.",
    },
    name: "Letizia",
    service: { it: "Ceretta", en: "Waxing" },
    when: { it: "più di un anno fa", en: "more than a year ago" },
  },
];

const Stars: React.FC<{ rating: number; lang: Lang }> = ({ rating, lang }) => (
  <span
    className="lvs-reviews__stars"
    aria-label={lang === "en" ? `${rating} out of 5` : `${rating} su 5`}
  >
    {Array.from({ length: 5 }).map((_, i) => (
      <StarFilled
        key={i}
        className={i < rating ? "is-on" : "is-off"}
        aria-hidden="true"
      />
    ))}
  </span>
);

const Testimonials: React.FC = () => {
  const feature = reviews.find((r) => r.feature)!;
  const rest = reviews.filter((r) => !r.feature);
  const { lang } = useLang();
  const isEn = lang === "en";

  const total = reviews.length;
  const average =
    Math.round((reviews.reduce((s, r) => s + r.rating, 0) / total) * 10) / 10;

  const headRef = useReveal<HTMLElement>();
  const featureRef = useReveal<HTMLElement>();
  const gridRef = useReveal<HTMLUListElement>();
  const moreRef = useReveal<HTMLAnchorElement>();

  return (
    <section className="lvs-reviews">
      <div className="lvs-reviews__inner lvs-container">
        <header className="lvs-reviews__head lvs-reveal" ref={headRef}>
          <span className="eyebrow lvs-reviews__eyebrow">
            {isEn
              ? "Reviews · verified on Treatwell and Google"
              : "Recensioni · verificate su Treatwell e Google"}
          </span>
          <h2 className="lvs-reviews__title">
            {isEn ? (
              <>
                What they say about <em>Regina.</em>
              </>
            ) : (
              <>
                Cosa dicono di <em>Regina.</em>
              </>
            )}
          </h2>
          <div className="lvs-reviews__meta">
            <div className="lvs-reviews__score">
              <Stars rating={Math.round(average)} lang={lang} />
              <span className="lvs-reviews__avg">{average.toFixed(1)}</span>
            </div>
            <span className="lvs-reviews__count">
              {total} {isEn ? "verified reviews" : "recensioni verificate"}
            </span>
          </div>
        </header>

        <figure className="lvs-reviews__feature lvs-reveal" ref={featureRef}>
          <Stars rating={feature.rating} lang={lang} />
          <blockquote>
            <p>{pick(feature.body, lang)}</p>
          </blockquote>
          <figcaption>
            <cite>{feature.name}</cite>
            <span className="lvs-reviews__chip">
              {pick(feature.service, lang)}
            </span>
            <span className="lvs-reviews__when">
              {pick(feature.when, lang)}
            </span>
          </figcaption>
        </figure>

        <ul className="lvs-reviews__grid lvs-reveal-group" ref={gridRef}>
          {rest.map((r, i) => (
            <li key={`${r.name}-${i}`} className="lvs-reviews__card">
              <Stars rating={r.rating} lang={lang} />
              <p className="lvs-reviews__body">{pick(r.body, lang)}</p>
              <div className="lvs-reviews__foot">
                <cite>{r.name}</cite>
                <span className="lvs-reviews__chip">{pick(r.service, lang)}</span>
                <span className="lvs-reviews__when">{pick(r.when, lang)}</span>
              </div>
            </li>
          ))}
        </ul>

        <a
          className="lvs-reviews__more lvs-reveal lvs-reveal--fade"
          ref={moreRef}
          href="https://www.treatwell.it/salone/laviens-wellness/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => conversions.treatwell()}
        >
          {isEn
            ? "Read all reviews on Treatwell →"
            : "Leggi tutte le recensioni su Treatwell →"}
        </a>
      </div>
    </section>
  );
};

export default Testimonials;
